import type { ReactNode } from 'react'
import { Building2 } from 'lucide-react'
import { TrimmedLogo } from '@/components/layout/TrimmedLogo'
import { poweredByHref } from '@/services/api/systemBranding'
import type { SchoolDesignPublic } from '@/services/api/jumppage'

function schoolLoginSurfaceColor(theme: string, bgColor: string): string {
  return theme === 'dark' ? bgColor : '#ffffff'
}

interface SchoolLoginFrameProps {
  design: SchoolDesignPublic
  schoolName: string
  title: string
  subtitle?: string
  systemLogo?: string | null
  systemLogoUrl?: string | null
  children: ReactNode
  compact?: boolean
}

export function SchoolLoginFrame({
  design,
  schoolName,
  title,
  subtitle,
  systemLogo,
  systemLogoUrl,
  children,
  compact = false,
}: SchoolLoginFrameProps) {
  const surfaceColor = schoolLoginSurfaceColor(design.theme, design.bg_color)
  const muted = design.muted_color
  const logoVisible = design.logo_position !== 'none'
  const logoSrc = design.logo_data || design.logo_url
  const logoAlign = design.logo_position === 'left' ? 'justify-start' : 'justify-center'
  const href = poweredByHref(systemLogoUrl)

  const inner = (
    <div className={`${compact ? 'w-full' : 'max-w-lg w-full'} space-y-8 relative`}>
      <div
        data-testid="school-login-card"
        className="rounded-xl shadow-xl p-8 space-y-8"
        style={{ borderWidth: 1, borderStyle: 'solid', borderColor: design.border_color, backgroundColor: surfaceColor }}
      >
        {logoVisible && logoSrc && (
          <div className={`flex ${logoAlign} mb-4`}>
            <img src={logoSrc} alt={schoolName} className="h-16 max-w-xs object-contain" />
          </div>
        )}
        {logoVisible && !logoSrc && (
          <div className={`flex ${logoAlign} mb-4`}>
            <Building2 data-testid="school-login-logo-fallback" className="h-16 text-gray-400" />
          </div>
        )}
        {design.show_school_name && (
          <p className="text-center text-sm font-medium mb-2" style={{ color: muted }}>{schoolName}</p>
        )}
        <h2 className="mt-2 text-3xl font-extrabold text-center" style={{ color: design.text_color }}>{title}</h2>
        {subtitle ? <p className="mt-2 text-sm text-center" style={{ color: muted }}>{subtitle}</p> : null}
        {children}
      </div>

      {systemLogo && (
        <footer className="text-center mt-8 text-xs" style={{ color: muted }}>
          <a href={href} target="_blank" rel="noopener noreferrer" aria-label="Powered by">
            <TrimmedLogo src={systemLogo} alt="Powered by" variant="light" className="h-16 max-w-[280px] object-contain inline-block" />
          </a>
        </footer>
      )}
    </div>
  )

  if (compact) {
    return (
      <div className="p-6 flex justify-center" style={{ backgroundColor: design.bg_color }}>
        {inner}
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: design.bg_color }}
    >
      {inner}
    </div>
  )
}
